import { useWidgetSettings } from './useWidgetSettings'

function createInstance() {
  const { features } = useFeatures()
  const { isAuthenticated, currentCustomerId, sessionData, currentProviderTheme, unagreedPolicies } = useSessionData()
  const { isWidget, widgetSettingsOptionsType, widgetSettingsOptionsSpecificPages } = useWidgetSettings()

  const useNewCalendar = features.customer_v2_schedule && !(features.customer_v2_calendar_allow_user_toggle && !features.customer_v2_calendar_user_toggle)
  const hasUnagreedPolicies = unagreedPolicies?.length > 0
  const useWidgetPageVisibility = isWidget && widgetSettingsOptionsType === 'specific_pages'

  const dashboardRoute = {
    path: '/dashboard',
    v2Path: true,
  }

  const specificPageKeyToRouteKey = {
    'schedule': 'schedule',
    'schedule.month': 'schedule.month',
    'schedule.day': 'schedule.day',
    'schedule.week': 'schedule.week',
    'my_account': 'my_account',
    'my_account.upcoming_registrations': 'my_account.upcoming_registrations',
    'my_account.profile': 'my_account.profile',
    'my_account.memberships': 'my_account.memberships',
    'my_account.registrations': 'my_account.registrations',
    'my_account.reservations': 'my_account.reservations',
    'my_account.referrals': 'my_account.referrals',
    'my_account.payments': 'my_account.payments',
    'my_account.documents': 'my_account.documents',
    'my_account.notifications': 'my_account.notifications',
    'my_account.contact': 'my_account.contact',
    'memberships': 'memberships',
    'store': 'store',
    'content': 'on_demand',
    'on_demand': 'on_demand',
    'appointments': 'appointments',
    'forms_and_waivers': 'forms_and_waivers',
    'courses': 'courses',
  }

  const scheduleFeatureRoute = useNewCalendar
    ? {
        path: '/schedule',
        v2Path: true,
      }
    : {
        path: '/',
        pathMatch: new RegExp('^/$'),
        v2Path: false,
      }

  // TODO: Should this be replaced with vue router / route meta? Does route meta support dynamic values?
  const featureRoutes = [
    {
      routeKey: 'my_account.upcoming_registrations',
      path: '/account/upcoming_registrations',
      v2Path: true,
    },
    {
      routeKey: 'schedule',
      ...scheduleFeatureRoute,
      visible: currentProviderTheme?.visible_sections?.schedule && !hasUnagreedPolicies,
    },
    {
      routeKey: 'memberships',
      path: '/memberships',
      v2Path: false,
      visible: !!currentProviderTheme?.visible_sections?.memberships && !hasUnagreedPolicies,
    },
    {
      routeKey: 'documents',
      path: '/documents',
      v2Path: false,
    },
    {
      routeKey: 'store',
      path: '/store',
      v2Path: features?.customer_store_v2,
      visible: !!sessionData?.store?.products_count && !hasUnagreedPolicies,
    },
    {
      routeKey: 'on_demand',
      path: '/on_demand',
      v2Path: true,
      visible: features.on_demand && !!sessionData?.on_demand_content?.exists && !hasUnagreedPolicies,
    },
    {
      routeKey: 'appointments',
      path: '/appointment_types',
      v2Link: features.customer_appointments_v2,
      visible: features.customer_appointments_v2 && !!sessionData?.appointments?.has_visible_types && !hasUnagreedPolicies,
    },
    {
      routeKey: 'forms_and_waivers',
      path: '/prerequisites/onboarding',
      v2Path: features?.waiver_signatures,
      visible: isAuthenticated && !!currentProviderTheme?.visible_sections?.requirements && !hasUnagreedPolicies,
    },
    {
      routeKey: 'courses',
      path: '/courses',
      v2Path: false,
      visible: !!currentProviderTheme?.visible_sections?.courses && !!sessionData?.courses?.has_active && !hasUnagreedPolicies,
    },
    {
      routeKey: 'my_account.profile',
      path: `/account/customers/${currentCustomerId}`,
      pathMatch: new RegExp('^/account/customers'),
      v2Path: true,
      visible: isAuthenticated,
    },
    {
      routeKey: 'my_account.memberships',
      path: '/account/memberships',
      v2Path: true,
      visible: isAuthenticated,
    },
    {
      routeKey: 'my_account.reservations',
      path: '/account/reservations',
      v2Path: true,
      visible: isAuthenticated && !!sessionData?.reservations?.exists,
    },
    {
      routeKey: 'my_account.registrations',
      path: '/account/registrations',
      v2Path: true,
      visible: isAuthenticated,
    },
    {
      routeKey: 'my_account.payments',
      path: '/account/payments',
      v2Path: true,
      visible: isAuthenticated,
    },
    {
      routeKey: 'my_account.documents',
      path: '/account/documents',
      v2Path: true,
      visible: isAuthenticated,
    },
    {
      routeKey: 'my_account.referrals',
      path: '/account/referrals',
      v2Path: true,
      visible: isAuthenticated && !!features?.customer_referrals,
    },
    {
      routeKey: 'my_account.notifications',
      path: '/account/notifications_settings',
      v2Path: true,
      visible: isAuthenticated,
    },
    {
      routeKey: 'my_account.contact',
      path: '/account/contact',
      v2Path: true,
      visible: isAuthenticated,
    },
    {
      routeKey: 'my_account',
      path: '/account',
      pathMatch: new RegExp('^/account'),
      v2Path: true,
      visible: isAuthenticated,
    },
  ]
    .map(item => ({
      ...item,
      pathMatch: item.pathMatch || new RegExp(`^${item.path}`),
    }))
    .map(item => useWidgetPageVisibility
      ? {
          ...item,
          visible: item.visible !== false && isWidgetPageVisible(item.routeKey),
        }
      : item)

  function isWidgetPageVisible(routeKeyQuery) {
    return widgetSettingsOptionsSpecificPages.some((specificPageKey) => {
      const routeKey = specificPageKeyToRouteKey[specificPageKey]
      return routeKey.startsWith(routeKeyQuery)
    })
  }

  const visibleFeatureRoutes = featureRoutes.filter(item => item.visible !== false)

  const firstVisibleFeatureRoute = visibleFeatureRoutes[0]

  const getFeatureRouteByPath = (path) => {
    return featureRoutes.find(item => item.pathMatch.test(path))
  }

  const getFeatureRouteByKey = (routeKey) => {
    return featureRoutes.find(item => routeKey.startsWith(item.routeKey))
  }

  const getClosestVisibleFeatureRouteByKey = (routeKey) => {
    const featureRoute = visibleFeatureRoutes.find(item => item.routeKey.startsWith(routeKey))
    if (featureRoute)
      return featureRoute
    if (routeKey.includes('.')) {
      const [parentRouteKey] = routeKey.split('.')
      return getClosestVisibleFeatureRouteByKey(parentRouteKey)
    }
    if (firstVisibleFeatureRoute)
      return firstVisibleFeatureRoute
    return dashboardRoute
  }

  const getClosestVisibleFeatureRouteByPath = (path) => {
    const featureRoute = featureRoutes.find(item => item.pathMatch.test(path))
    if (featureRoute) {
      if (featureRoute.visible !== false)
        return featureRoute
      if (featureRoute.routeKey.includes('.')) {
        const [parentRouteKey] = featureRoute.routeKey.split('.')
        return getClosestVisibleFeatureRouteByKey(parentRouteKey)
      }
    }
    if (firstVisibleFeatureRoute)
      return firstVisibleFeatureRoute
    return dashboardRoute
  }

  const getFeatureRouteVisibilityByKey = (routeKey) => {
    return featureRoutes
      .filter(item => item.routeKey.startsWith(routeKey))
      .some(item => item.visible !== false)
  }

  return {
    featureRoutes,
    visibleFeatureRoutes,
    firstVisibleFeatureRoute,
    getFeatureRouteByPath,
    getFeatureRouteByKey,
    getClosestVisibleFeatureRouteByKey,
    getClosestVisibleFeatureRouteByPath,
    getFeatureRouteVisibilityByKey,
  }
}

export const useFeatureRoutes = useSingleton(createInstance)
